import { createApi } from "@reduxjs/toolkit/query/react";
import { CURRENCIES, VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import { gtcAcceptHeaders } from "helpers/gtcClientContext";
import type { SupportingDocument } from "types";
import type { BuyerRequestSignature } from "helpers/buyerRequestSignature";
import type { Ewallet, Pagination } from "types/ewallet";

import type {
  AddInvoice,
  AddInvoiceDetails,
  ApproveListing,
  CompanyProfile,
  MapProduct,
  MarketplaceDraftInvoiceResponse,
  MarketplaceInvoiceListResponse,
  MarketplaceCurrency,
  PHASE,
  PickList,
  Stats,
  Transaction,
  WithdrawalDeposit,
  MarketplacePartnerGroupedByCurrency,
  UploadedInvoice,
} from "./api-marketplace.d";

type SignBuyerRequestPayload = BuyerRequestSignature & { invoice_id: string };

export interface BuyerRequestSignatureResponse {
  id: string;
  signature_id?: string;
  invoice_submission_required?: boolean;
}

const formatCurrencies = (accountCurrencies: string[]) => {
  const currencies = Object.entries(CURRENCIES)
    ?.filter(([key]) => !accountCurrencies || accountCurrencies.includes(key))
    .map(([key, value]) => ({
      label: key,
      value: key,
      ...value,
    }));
  return currencies;
};

const getInvoiceListParams = (
  phase: PHASE,
  mapparams: string,
  map_id?: string,
) => {
  const params = new URLSearchParams(mapparams);

  params.set("phase", phase);
  if (map_id) {
    params.set("map_id", map_id);
  } else {
    params.delete("map_id");
  }

  Array.from(params.entries()).forEach(([key, value]) => {
    if (value === "") {
      params.delete(key);
    }
  });

  return params;
};

export const marketplace = createApi({
  reducerPath: "marketplace",
  tagTypes: [
    "INVOICES",
    "PICKLIST",
    "SUPPORTING_DOCS",
    "EWALLETS",
    "TRANSACTIONS",
    "INCOMPLETE_WITHDRAWAL_DEPOSITS",
    "MAP_DETAILS",
    "CURRENCIES",
    "GTC",
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/marketplace`,
  }),
  endpoints: (builder) => ({
    getMarketplacePartners: builder.query<
      MarketplacePartnerGroupedByCurrency,
      string
    >({
      query: (id) => ({ url: `/accounts/${id}/partners`, method: "get" }),
      transformResponse: (response) => response?.data,
    }),

    getGtcApprovalStats: builder.query<
      boolean,
      { accountId: string; gtcType: string }
    >({
      query: ({ accountId, gtcType }) => ({
        url: `/accounts/${accountId}/gtc/approval-stats`,
        method: "get",
        params: { gtc_type: gtcType },
      }),
      providesTags: (_result, _error, { accountId, gtcType }) => [
        { type: "GTC", id: `${accountId}:${gtcType}` },
      ],
      transformResponse: (response) => response?.data,
    }),
    submitGtc: builder.mutation<any, { id: string; gtcType: string }>({
      query: ({ id, gtcType }) => ({
        url: `/accounts/${id}/gtc/accept`,
        method: "POST",
        data: { gtc_type: gtcType },
        headers: gtcAcceptHeaders(),
      }),
      invalidatesTags: (_result, _error, { id, gtcType }) => [
        { type: "GTC", id: `${id}:${gtcType}` },
      ],
      transformResponse: (response) => response?.data,
    }),

    getInvoiceListByUser: builder.query<
      MarketplaceInvoiceListResponse,
      {
        account_id: string;
        partner_id: string;
        phase: PHASE;
        mapparams: string;
        map_id?: string;
      }
    >({
      query: ({ account_id, partner_id, phase, mapparams, map_id }) => ({
        url: `/accounts/${account_id}/partners/${partner_id}/invoices`,
        method: "get",
        params: getInvoiceListParams(phase, mapparams, map_id),
      }),
      transformResponse: (response) => {
        const payload = response?.data ?? response;
        if (Array.isArray(payload)) {
          return { invoices: payload };
        }
        return payload;
      },
      providesTags: ["INVOICES"],
    }),

    getMarketplaceAccountInvoices: builder.query<any, string>({
      query: (accountId) => ({
        url: `/accounts/${accountId}/invoices`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["INVOICES"],
    }),

    getPartnerMarketplaceDetails: builder.query<
      CompanyProfile,
      { account_id: string; partner_id: string; map_id?: string }
    >({
      query: ({ account_id, partner_id, map_id }) => ({
        url: `/accounts/${account_id}/partners/${partner_id}/map-details`,
        method: "get",
        params: map_id ? { map_id } : undefined,
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["INVOICES"],
    }),
    getPicklists: builder.query<PickList, null>({
      query: () => ({ url: `/picklists`, method: "get" }),
      transformResponse: (response) => response?.data,
      providesTags: ["PICKLIST"],
    }),
    getCurrencies: builder.query<MarketplaceCurrency[], string>({
      query: (id) => ({ url: `/accounts/${id}/currencies`, method: "get" }),
      transformResponse: (response) => formatCurrencies(response?.data),
      providesTags: ["CURRENCIES"],
    }),
    getAgentCurrencies: builder.query<MarketplaceCurrency[], string>({
      query: (agentId) => ({
        url: `/agents/${agentId}/currencies`,
        method: "get",
      }),
      transformResponse: (response) => formatCurrencies(response?.data),
      providesTags: ["CURRENCIES"],
    }),
    getTransactions: builder.query<
      { pagination: Pagination; transactions: Transaction[] },
      any
    >({
      query: (params) => {
        const {
          accountId,
          ewalletId,
          page,
          per_page,
          search,
          effective_date,
          transaction_type,
          status,
          currency,
        } = params;

        const queryParams = new URLSearchParams();
        queryParams.append("ewallet_id", ewalletId);
        queryParams.append("page", String(page || 1));
        queryParams.append("per_page", String(per_page || 10));

        if (search) {
          queryParams.append("search", search);
        }

        if (effective_date) {
          queryParams.append("effective_date", effective_date);
        }

        if (transaction_type) {
          queryParams.append("transaction_type", transaction_type);
        }

        if (status) {
          queryParams.append("status", status);
        }

        if (currency) {
          queryParams.append("currency", currency);
        }

        return {
          url: `/accounts/${accountId}/transactions?${queryParams.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response?.data,
      providesTags: ["TRANSACTIONS"],
    }),
    getIncompleteWithdrawalDeposits: builder.query<
      {
        withdrawal_deposits: Array<{
          id: string;
          ewallet_id: string;
          amount: number;
          currency: string;
          transaction_type: string;
          status: string;
          created_at: string | null;
          effective_date: string | null;
        }>;
      },
      { accountId: string; ewalletId: string }
    >({
      query: ({ accountId, ewalletId }) => ({
        url: `/accounts/${accountId}/ewallets/${ewalletId}/incomplete`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["INCOMPLETE_WITHDRAWAL_DEPOSITS"],
    }),
    getAgentTransactions: builder.query<
      { pagination: Pagination; transactions: Transaction[] },
      any
    >({
      query: (params) => {
        const {
          ewalletId,
          page,
          per_page,
          search,
          effective_date,
          transaction_type,
          status,
          currency,
        } = params;

        const queryParams = new URLSearchParams();
        queryParams.append("ewallet_id", ewalletId);
        queryParams.append("page", String(page || 1));
        queryParams.append("per_page", String(per_page || 10));

        if (search) {
          queryParams.append("search", search);
        }

        if (effective_date) {
          queryParams.append("effective_date", effective_date);
        }

        if (transaction_type) {
          queryParams.append("transaction_type", transaction_type);
        }

        if (status) {
          queryParams.append("status", status);
        }

        if (currency) {
          queryParams.append("currency", currency);
        }

        return {
          url: `/agents/transactions?${queryParams.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response?.data,
      providesTags: ["TRANSACTIONS"],
    }),
    getCommonKeys: builder.query<
      {
        TRANSACTION_TYPE: string[];
        [key: string]: string[];
      },
      void
    >({
      query: () => ({
        url: `/common-keys`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    getStats: builder.query<
      Stats,
      {
        account_id: string;
        currency: string;
        partner_id: string;
        map_id?: string;
      }
    >({
      query: ({ account_id, currency, partner_id, map_id }) => ({
        url: `/accounts/${account_id}/partners/${partner_id}/invoices/stats`,
        method: "get",
        params: {
          currency,
          ...(map_id ? { map_id } : {}),
        },
      }),
      transformResponse: (response) => response?.data,
    }),
    getSupportingDocuments: builder.query<
      SupportingDocument[],
      { accountId: string; partnerId: string; mapId?: string }
    >({
      query: (params) => ({
        url: `/accounts/${params.accountId}/partners/${params.partnerId}/supporting-documents`,
        method: "get",
        params: { map_id: params.mapId },
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SUPPORTING_DOCS"],
    }),
    getInvoiceSupportingDocuments: builder.query<SupportingDocument[], string>({
      query: (invoiceId) => ({
        url: `/invoices/${invoiceId}/supporting-documents`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SUPPORTING_DOCS"],
    }),
    getUploadedSupportingDocuments: builder.query<
      {
        invoice_id: string;
        document_type: string;
        attachments: string[];
        document_id: string;
        required_document_id?: string | null;
        file_name: string;
        upload_date: string;
        map_invoice_required_document_id: string | null;
        required: boolean;
      }[],
      string
    >({
      query: (invoice_id) => ({
        url: `/invoices/${invoice_id}/documents`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SUPPORTING_DOCS"],
    }),
    getUploadedSupportingDocumentMetadata: builder.query<
      Omit<{
        invoice_id: string;
        document_type: string;
        attachments: string[];
        document_id: string;
        required_document_id?: string | null;
        file_name: string;
        upload_date: string;
        map_invoice_required_document_id: string | null;
        required: boolean;
      }, "attachments">[],
      string
    >({
      query: (invoice_id) => ({
        url: `/invoices/${invoice_id}/documents`,
        method: "get",
         params: { metadata_only: true },
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["SUPPORTING_DOCS"],
    }),
    getInvoiceById: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `/invoices/${invoice_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getDraftInvoiceById: builder.query<MarketplaceDraftInvoiceResponse, string>({
      query: (invoice_id) => ({
        url: `/invoices/${invoice_id}?draft=true`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getInvoiceCount: builder.query<
      any,
      {
        account_id: string;
        partner_id: string;
        map_id?: string;
      }
    >({
      query: ({ account_id, partner_id, map_id }) => ({
        url: `/accounts/${account_id}/partners/${partner_id}/invoice_count${
          map_id ? `?map_id=${encodeURIComponent(map_id)}` : ""
        }`,
        method: "get",
      }),
      transformResponse: (response) => response?.data ?? response,
      providesTags: ["INVOICES"],
    }),
    addInvoice: builder.mutation<{ id: string }, AddInvoice>({
      query: (data) => {
        const { account_id, partner_id, invoice_details } = data;
        return {
          url: `/accounts/${account_id}/partners/${partner_id}/invoices`,
          method: "POST",
          data: invoice_details,
        };
      },
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    updateInvoice: builder.mutation<
      { id: string },
      { invoiceId: string; invoiceDetails: AddInvoiceDetails }
    >({
      query: (data) => {
        const { invoiceId, invoiceDetails } = data;
        return {
          url: `/invoices/${invoiceId}`,
          method: "PUT",
          data: invoiceDetails,
        };
      },
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    submitInvoice: builder.mutation<string, string>({
      query: (invoiceId) => {
        return {
          url: `/invoices/${invoiceId}/submit`,
          method: "POST",
          data: { approve: true },
        };
      },
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    addInvoiceBatch: builder.mutation<
      { invoice_ids: UploadedInvoice[]; error_messages: string[] },
      { account_id: string; partner_id: string; invoices: AddInvoiceDetails[] }
    >({
      query: (data) => {
        const { account_id, partner_id, invoices } = data;
        return {
          url: `/accounts/${account_id}/partners/${partner_id}/batch-invoices`,
          method: "POST",
          data: invoices,
        };
      },
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    submitBatchInvoice: builder.mutation<string, string[]>({
      query: (invoiceIds) => {
        return {
          url: `/batch-invoices/submit`,
          method: "POST",
          data: invoiceIds,
        };
      },
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    submitWithdrawal: builder.mutation<
      any,
      { id: string; data: WithdrawalDeposit }
    >({
      query: ({ id, data }) => {
        return {
          url: `/accounts/${id}/withdrawal-deposit`,
          method: "POST",
          data,
        };
      },
      invalidatesTags: ["EWALLETS", "INCOMPLETE_WITHDRAWAL_DEPOSITS"],
      transformResponse: (response) => response?.data,
    }),
    submitAgentWithdrawal: builder.mutation<
      any,
      { id: string; data: WithdrawalDeposit }
    >({
      query: ({ id, data }) => {
        return {
          url: `/agents/${id}/withdrawal-deposit`,
          method: "POST",
          data,
        };
      },
      invalidatesTags: ["EWALLETS", "INCOMPLETE_WITHDRAWAL_DEPOSITS"],
      transformResponse: (response) => response?.data,
    }),
    approveListing: builder.mutation<any, { id: string; data: ApproveListing }>(
      {
        query: ({ id, data }) => {
          return {
            url: `/accounts/${id}/approve-listing`,
            method: "POST",
            data,
          };
        },
        invalidatesTags: ["INVOICES"],
        transformResponse: (response) => response?.data,
      },
    ),
    postInvoiceAdditionalDocument: builder.mutation<
      {
        id: string;
        name: string;
        document_type: string;
        required_document_id: string;
        submitted_at: string;
        url?: string;
      },
      { file: File; required_document_id: string; invoice_id: string }
    >({
      query: ({ file, required_document_id, invoice_id }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("required_document_id", required_document_id);
        return {
          url: `/invoices/${encodeURIComponent(invoice_id)}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      invalidatesTags: ["SUPPORTING_DOCS"],
      transformResponse: (response) => response?.data,
    }),
    postSupportingDocuments: builder.mutation<
      {
        id: string;
        name: string;
        document_type: string;
        required_document_id?: string | null;
        submitted_at: string;
      },
      { file: File; supporting_document_id: string; invoice_id: string }
    >({
      query: (data) => {
        const formData = new FormData();
        formData.append("file", data.file);
        formData.append("required_document_id", data.supporting_document_id);
        return {
          url: `/invoices/${data.invoice_id}/documents`,
          method: "POST",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        };
      },
      invalidatesTags: ["SUPPORTING_DOCS"],
      transformResponse: (response) => response?.data,
      async onQueryStarted(
        { invoice_id, supporting_document_id },
        { dispatch, getState, queryFulfilled },
      ) {
        try {
          const { data: uploaded } = await queryFulfilled;
          const requirement = marketplace.endpoints.getInvoiceSupportingDocuments
            .select(invoice_id)(getState() as any)
            ?.data?.find(
              (document) =>
                document.required_document_id === supporting_document_id ||
                document.map_invoice_required_document_id === supporting_document_id,
            );
          dispatch(
            marketplace.util.updateQueryData(
              "getUploadedSupportingDocumentMetadata",
              invoice_id,
              (draft) => {
                const matchingDocument = {
                  invoice_id,
                  document_type: uploaded.document_type || supporting_document_id,
                  document_id: uploaded.id,
                  required_document_id:
                    uploaded.required_document_id ?? supporting_document_id,
                  file_name: uploaded.name,
                  upload_date: uploaded.submitted_at,
                  map_invoice_required_document_id:
                    requirement?.map_invoice_required_document_id ?? null,
                  required: requirement?.required ?? true,
                };
                const sameSlot = (document: (typeof draft)[number]) =>
                  document.required_document_id === supporting_document_id ||
                  document.map_invoice_required_document_id === supporting_document_id;
                const existingIndex = draft.findIndex(
                  (document) => document.document_id === uploaded.id,
                );
                if (existingIndex >= 0) {
                  draft[existingIndex] = matchingDocument;
                } else {
                  for (let index = draft.length - 1; index >= 0; index -= 1) {
                    if (sameSlot(draft[index])) draft.splice(index, 1);
                  }
                  draft.push(matchingDocument);
                }
              },
            ),
          );
        } catch {
          // The active query's tag invalidation will reconcile the cache after upload.
        }
      },
    }),
    deleteSupportingDocument: builder.mutation<
      any,
      { invoice_id: string; document_id: string }
    >({
      query: ({ invoice_id, document_id }) => ({
        url: `/invoices/${invoice_id}/documents/${document_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SUPPORTING_DOCS"],
      transformResponse: (response) => response?.data,
    }),
    getMapProducts: builder.query<
      // not used
      MapProduct,
      { account_id: string; partner_id: string; currency: string }
    >({
      query: ({ account_id, partner_id, currency }) => ({
        url: `/${account_id}/products?partner_id=${partner_id}&currency=${currency}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getAccountBanks: builder.query<
      {
        id: string;
        name: string;
        bank_name: string;
        bank_account_number: string;
        bank_address: string;
        beneficiary_name: string;
        iban: string;
        swift_code: string;
        currency: string;
        sf_bank_account_id: string;
      }[],
      string
    >({
      // will be used in withdrawal
      query: (account_id) => ({
        url: `/accounts/${account_id}/banks`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    generateSupplierRequest: builder.mutation<
      string,
      {
        invoice_id: string;
        bank: {
          bank_name: string;
          bank_account_number: string;
          address: string;
          beneficiary_name: string;
          pre_payment: boolean;
        };
      }
    >({
      query: (data) => {
        return {
          url: `/invoices/${data.invoice_id}/supplier-request`,
          method: "POST",
          data: data.bank,
        };
      },
      transformResponse: (response) => response?.data,
    }),
    getAgentBanks: builder.query<
      {
        id: string;
        name: string;
        bank_name: string;
        bank_account_number: string;
        bank_address: string;
        beneficiary_name: string;
        iban: string;
        swift_code: string;
        currency: string;
        sf_bank_account_id: string;
      }[],
      string
    >({
      query: (agentId) => ({
        url: `/agents/${agentId}/banks`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    signSupplierRequest: builder.mutation<
      string,
      {
        invoice_id: string;
        bank: {
          bank_name: string;
          bank_account_number: string;
          address: string;
          beneficiary_name: string;
          pre_payment: boolean;
        };
      }
    >({
      query: (data) => {
        return {
          url: `/invoices/${data.invoice_id}/supplier-request/signature`,
          method: "POST",
          data: data.bank,
        };
      },
      transformResponse: (response) => response?.data,
    }),
    getBuyerRequest: builder.query<string, string>({
      query: (invoice_id) => ({
        url: `/invoices/${invoice_id}/buyer-request`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    signBuyerRequest: builder.mutation<BuyerRequestSignatureResponse, SignBuyerRequestPayload>({
      query: ({ invoice_id, ...signature }) => ({
        url: `/invoices/${invoice_id}/buyer-request/signature`,
        method: "POST",
        data: signature,
      }),
      invalidatesTags: ["INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    getEwallet: builder.query<Ewallet[], string>({
      query: (id) => {
        return {
          url: `/accounts/${id}/ewallets`,
          method: "get",
        };
      },
      providesTags: ["EWALLETS"],
      transformResponse: (response) => response?.data,
    }),
    getAgentEwallets: builder.query<Ewallet[], void>({
      query: () => ({
        url: `/agents/ewallets`,
        method: "get",
      }),
      providesTags: ["EWALLETS"],
      transformResponse: (response) => response?.data,
    }),
    getTaxInvoices: builder.query<any, string>({
      query: (invoiceId) => ({
        url: `/invoices/${invoiceId}/tax-invoices`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
    getTaxInvoiceFile: builder.query<
      Blob,
      { invoiceId: string; taxInvoiceId: string }
    >({
      // Use a custom queryFn to handle blob response
      async queryFn({ invoiceId, taxInvoiceId }) {
        try {
          const token = JSON.parse(
            localStorage.getItem("token") || "{}",
          )?.value;
          const response = await import("axios").then(({ default: axios }) =>
            axios.get(
              `${VITE_API_V2_URL}/marketplace/invoices/${invoiceId}/tax-invoices/${taxInvoiceId}/file`,
              {
                responseType: "blob",
                headers: {
                  Authorization: `Bearer ${token}`,
                  Accept: "application/pdf",
                },
              },
            ),
          );
          return { data: response?.data };
        } catch (error) {
          return { error };
        }
      },
    }),
  }),
});

export const {
  useGetMarketplacePartnersQuery,
  useGetGtcApprovalStatsQuery,
  useSubmitGtcMutation,
  useGetInvoiceListByUserQuery,
  useGetInvoiceByIdQuery,
  useGetDraftInvoiceByIdQuery,
  useGetInvoiceCountQuery,
  useAddInvoiceMutation,
  useUpdateInvoiceMutation,
  useSubmitInvoiceMutation,
  useAddInvoiceBatchMutation,
  useSubmitBatchInvoiceMutation,
  useGetPicklistsQuery,
  useGetSupportingDocumentsQuery,
  useGetInvoiceSupportingDocumentsQuery,
  useLazyGetInvoiceSupportingDocumentsQuery,
  usePostInvoiceAdditionalDocumentMutation,
  usePostSupportingDocumentsMutation,
  useDeleteSupportingDocumentMutation,
  useGetStatsQuery,
  useGetCurrenciesQuery,
  useGetAgentCurrenciesQuery,
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useGetIncompleteWithdrawalDepositsQuery,
  useGetAgentTransactionsQuery,
  useLazyGetAgentTransactionsQuery,
  useSubmitWithdrawalMutation,
  useSubmitAgentWithdrawalMutation,
  useApproveListingMutation,
  useGetUploadedSupportingDocumentsQuery,
  useGetUploadedSupportingDocumentMetadataQuery,
  useLazyGetUploadedSupportingDocumentMetadataQuery,
  useGetMapProductsQuery,
  useGetPartnerMarketplaceDetailsQuery,
  useGetAccountBanksQuery,
  useGetAgentBanksQuery,
  useGenerateSupplierRequestMutation,
  useSignSupplierRequestMutation,
  useGetBuyerRequestQuery,
  useSignBuyerRequestMutation,
  useLazyGetBuyerRequestQuery,
  useGetEwalletQuery,
  useGetAgentEwalletsQuery,
  useGetCommonKeysQuery,
  useGetMarketplaceAccountInvoicesQuery,
  useGetTaxInvoicesQuery,
  useLazyGetTaxInvoiceFileQuery,
} = marketplace;
